import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Calendar, Clock, Package, RotateCcw, Car, Ban, User, ArrowRightLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { JobPhaseBadge } from '@/components/tickets/JobPhaseBadge';
import { formatElapsedTime, formatTimeShort, getJobPhase } from '@/utils/bayScheduleHelpers';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { format, isToday } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
import { useSharedTimer } from '@/hooks/useSharedTimer';
import { LiftCapacityWarning } from '@/components/safety/LiftCapacityWarning';
import { LiftVerificationDialog } from '@/components/safety/LiftVerificationDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { CAPACITY_NOT_VERIFIED_TITLE, capacityNotVerifiedDescription, isCapacityNotVerified } from '@/lib/location/liftCapacityNotice';
import { useShopTimezone } from '@/contexts/TimezoneContext';

interface BayDetailSheetProps {
  bay: any | null;
  tickets: any[];
  technicians: any[];
  bays: any[];
  bayTypeLabels: Record<string, string>;
  isOpen: boolean;
  onClose: () => void;
  onTicketReassigned?: () => void;
  selectedDate: Date;
  readOnly?: boolean;
}

// Timer display component - only this re-renders every second
const CurrentTicketTimer = ({ ticket }: { ticket: any }) => {
  const tickTimestamp = useSharedTimer();
  
  if (ticket.status === 'Paused' || ticket.timer_status === 'paused') {
    return (
      <div className="text-center">
        <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
          ⏸ PAUSED
        </div>
        {ticket.pause_reason && (
          <div className="text-xs text-orange-500 dark:text-orange-400 mt-1">
            Reason: {ticket.pause_reason}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="text-2xl font-mono font-bold text-center text-primary">
      ⏱ {formatElapsedTime(new Date(ticket.actual_start_time), tickTimestamp)}
    </div>
  );
};

// Extracted to module scope so it has a STABLE identity across renders of BayDetailSheet.
// Previously this was defined inline inside BayDetailSheet, which caused the entire
// ticket card subtree (including the Dayboard/Details buttons) to unmount + remount
// on every parent re-render — making the buttons "flash" and become unselectable
// while the timer was ticking.
interface TicketCardProps {
  ticket: any;
  isCurrent?: boolean;
  technicians: any[];
  availableBays: any[];
  bayTypeLabels: Record<string, string>;
  reassigningTicketId: string | null;
  readOnly?: boolean;
  shopTimezone: string;
  getOptiFinish: (ticket: any) => Date | null;
  handleReassignBay: (ticketId: string, newBayId: string) => void;
  onNavigateDayboard: (ticketId: string) => void;
  onNavigateDetails: (ticketId: string) => void;
}

const TicketCard = ({
  ticket,
  isCurrent = false,
  technicians,
  availableBays,
  bayTypeLabels,
  reassigningTicketId,
  readOnly,
  shopTimezone,
  getOptiFinish,
  handleReassignBay,
  onNavigateDayboard,
  onNavigateDetails,
}: TicketCardProps) => {
  const { phase, partsEta, bayLocked } = getJobPhase(ticket);
  const assignedTech = technicians.find((t) => t.id === ticket.assigned_tech_id);
  const isReassigning = reassigningTicketId === ticket.id;

  return (
    <div
      className={cn(
        'p-3 rounded-lg border space-y-2',
        isCurrent ? 'bg-primary/5 border-primary/20' : 'bg-card'
      )}
    >
      {/* Status Badges Row */}
      <div className="flex flex-wrap gap-1.5">
        <Badge
          variant={ticket.type === 'Waiter' ? 'destructive' : 'secondary'}
          className="text-xs"
        >
          {ticket.type}
        </Badge>

        {ticket.is_comeback && (
          <Badge variant="outline" className="text-xs border-orange-500 text-orange-700 dark:text-orange-400">
            <RotateCcw className="h-3 w-3 mr-1" />
            Comeback
          </Badge>
        )}

        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            isVehiclePresent(ticket)
              ? 'border-green-500 text-green-700 dark:text-green-400'
              : 'border-red-500 text-red-700 dark:text-red-400'
          )}
        >
          {isVehiclePresent(ticket) ? (
            <>
              <Car className="h-3 w-3 mr-1" />
              Present
            </>
          ) : (
            <>
              <Ban className="h-3 w-3 mr-1" />
              Not Present
            </>
          )}
        </Badge>
      </div>

      {phase !== 'normal' && (
        <JobPhaseBadge phase={phase} partsEta={partsEta} bayLocked={bayLocked} className="text-xs" />
      )}

      <div>
        <p className="font-semibold">
          {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
        </p>
        <p className="text-sm text-muted-foreground">{ticket.customerName}</p>
        {ticket.ro_number && <p className="text-xs text-muted-foreground">RO #{ticket.ro_number}</p>}
      </div>

      {assignedTech && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="h-3 w-3" />
          <span>
            Assigned to: <span className="font-medium text-foreground">{assignedTech.name}</span>
          </span>
        </div>
      )}

      {isCurrent && ticket.actual_start_time && (
        <div className="p-2 bg-background rounded border">
          <CurrentTicketTimer ticket={ticket} />
        </div>
      )}

      <div className="space-y-1">
        <p className="text-xs font-semibold text-muted-foreground">Jobs:</p>
        {ticket.jobs?.map((job: any, idx: number) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <span className="w-1 h-1 rounded-full bg-primary/60 flex-shrink-0" />
            <span className="line-clamp-1">{job.name}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t text-xs">
        <div>
          <span className="text-muted-foreground">Start: </span>
          <span className="font-medium">
            {formatTimeShort(
              ticket.actual_start_time || ticket.start_time || ticket.scheduled_start_time,
              shopTimezone
            )}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">OptiFinish: </span>
          <span className="font-medium">{(() => { const f = getOptiFinish(ticket); return f ? formatTimeShort(f, shopTimezone) : 'Unknown'; })()}</span>
        </div>
      </div>

      {!readOnly && !isCurrent && availableBays.length > 0 && (
        <div className="pt-2 border-t">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <Select
              disabled={isReassigning}
              onValueChange={(newBayId) => handleReassignBay(ticket.id, newBayId)}
            >
              <SelectTrigger className="h-8 text-xs flex-1">
                {isReassigning ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Moving...</span>
                  </div>
                ) : (
                  <SelectValue placeholder="Reassign to bay..." />
                )}
              </SelectTrigger>
              <SelectContent className="bg-popover z-50">
                {availableBays.map((b) => (
                  <SelectItem key={b.id} value={b.id} className="text-xs">
                    <span className="font-medium">{b.name}</span>
                    <span className="text-muted-foreground ml-2">
                      {bayTypeLabels[b.bay_type] || b.bay_type}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {isCurrent && (
        <div className="flex gap-2 pt-2 border-t">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => onNavigateDayboard(ticket.id)}
          >
            <Calendar className="h-3 w-3 mr-1" />
            Dayboard
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs"
            onClick={() => onNavigateDetails(ticket.id)}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            Details
          </Button>
        </div>
      )}
    </div>
  );
};

export const BayDetailSheet = ({ 
  bay, 
  tickets, 
  technicians, 
  bays,
  bayTypeLabels,
  isOpen,
  onClose,
  onTicketReassigned,
  selectedDate,
  readOnly
}: BayDetailSheetProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { shopTimezone } = useShopTimezone();
  const [showCompleted, setShowCompleted] = useState(false);
  const [reassigningTicketId, setReassigningTicketId] = useState<string | null>(null);
  // CORE-AU: a move refused because the vehicle's weight is not verified for the lift.
  const { canEditShopFloor } = usePermissions();
  const [liftVerify, setLiftVerify] = useState<{ ticketId: string; bayId: string; bayName?: string } | null>(null);
  const [liftWarningData, setLiftWarningData] = useState<{
    gvwr: number;
    capacity: number;
    compatibleLifts: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }>;
    ticketId: string;
  } | null>(null);

  if (!bay) return null;

  // Filter tickets for this bay (including parked tickets that are scheduled here).
  // Chronomancer R-LOC-10: mirror canonical bay-occupancy index — exclude
  // parking_lot=true AND rollover descendants. Without this, future-day
  // Part B tickets (bay_id set + parking_lot=true future-state plan) leak
  // into today's bay detail sheet's "current/upcoming" lists.
  const bayTickets = tickets.filter(
    t => t.bay_id === bay.id && !t.parking_lot && !t.rollover_of_ticket_id
  );
  
  const currentTicket = bayTickets.find(t => 
    (t.status === 'Active' || t.status === 'Paused') && 
    (t.timer_status === 'running' || t.actual_start_time)
  );
  
  const upcomingTickets = bayTickets.filter(t => 
    (t.status === 'Active' || t.status === 'Paused') && 
    t.id !== currentTicket?.id
  ).sort((a, b) => {
    const aTime = new Date(a.start_time || a.scheduled_start_time).getTime();
    const bTime = new Date(b.start_time || b.scheduled_start_time).getTime();
    return aTime - bTime;
  });

  const completedTickets = bayTickets.filter(t => t.status === 'Completed');

  const technician = technicians.find(t => t.id === bay.assigned_tech_id);
  const techName = technician?.name || 'Unassigned';

  // Get available bays for reassignment (exclude current bay)
  const availableBays = bays.filter(b => b.id !== bay.id && b.is_active);

  const getOptiFinish = (ticket: any) => {
    // Use the canonical DB-stored opti_finish (matches Dayboard, includes AutoBump/pause/cascade)
    if (ticket.opti_finish) {
      return new Date(ticket.opti_finish);
    }
    // Fallback: calculate from predicted_minutes only. CORE-AC: with no booked duration the finish is unknown —
    // never an invented 60 minutes.
    const startTime = new Date(ticket.actual_start_time || ticket.start_time || ticket.scheduled_start_time);
    const minutes = ticket.predicted_minutes;
    if (!(typeof minutes === 'number' && minutes > 0)) return null;
    return new Date(startTime.getTime() + minutes * 60000);
  };

  const handleReassignBay = async (ticketId: string, newBayId: string) => {
    if (!newBayId || newBayId === bay.id) return;
    
    setReassigningTicketId(ticketId);
    
    try {
      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
        p_ticket_id: ticketId,
        p_target_bay_id: newBayId,
      });

      if (error) throw error;
      
      const result = rawData as unknown as { success: boolean; reason?: string; liftSafetyBlock?: boolean; liftSafetyUnverified?: boolean; liftRatingUnknown?: boolean; gvwr?: number; capacity?: number; compatibleLifts?: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }> };
      if (result && !result.success) {
        // OD-33 (revised): an unrated lift no longer refuses (capacityNotVerified is warned on success below).
        if (result.liftSafetyUnverified) {
          setLiftVerify({ ticketId, bayId: newBayId, bayName: bays.find(b => b.id === newBayId)?.name });
        } else if (result.liftSafetyBlock) {
          setLiftWarningData({
            gvwr: result.gvwr!,
            capacity: result.capacity!,
            compatibleLifts: result.compatibleLifts || [],
            ticketId,
          });
        } else {
          toast({
            title: "Cannot reassign bay",
            description: result.reason || 'Bay incompatible',
            variant: "destructive"
          });
        }
        // #15: a refusal means the view was behind — re-read the floor.
        onTicketReassigned?.();
        return;
      }

      const newBay = bays.find(b => b.id === newBayId);
      if (isCapacityNotVerified(result)) {
        // OD-33: allowed, never presented as safe.
        toast({ title: CAPACITY_NOT_VERIFIED_TITLE, description: capacityNotVerifiedDescription(newBay?.name) });
      } else {
        toast({
          title: "Ticket reassigned",
          description: `Moved to ${newBay?.name || 'new bay'}`
        });
      }
      
      onTicketReassigned?.();
    } catch (err: any) {
      console.error('Bay reassignment error:', err);
      toast({
        title: "Reassignment failed",
        description: err.message || "Could not reassign ticket to new bay",
        variant: "destructive"
      });
      onTicketReassigned?.();
    } finally {
      setReassigningTicketId(null);
    }
  };

  // Stable navigation handlers passed to the extracted TicketCard.
  const handleNavigateDayboard = (ticketId: string) => {
    navigate(`/?tab=dayboard&highlight=${ticketId}`);
    onClose();
  };

  const handleNavigateDetails = (ticketId: string) => {
    navigate(`/?tab=dayboard&highlight=${ticketId}&openDetails=true`);
    onClose();
  };

  const ticketCardProps = {
    technicians,
    availableBays,
    bayTypeLabels,
    reassigningTicketId,
    readOnly,
    shopTimezone,
    getOptiFinish,
    handleReassignBay,
    onNavigateDayboard: handleNavigateDayboard,
    onNavigateDetails: handleNavigateDetails,
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span>{bay.name}</span>
            <Badge variant="secondary" className="text-xs">
              {bayTypeLabels[bay.bay_type] || bay.bay_type}
            </Badge>
          </SheetTitle>
          <p className="text-sm text-muted-foreground">
            👤 {techName}
          </p>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Current Vehicle */}
          {currentTicket ? (
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Current Vehicle
              </h3>
              <TicketCard ticket={currentTicket} isCurrent {...ticketCardProps} />
            </div>
          ) : (
            <div className="p-4 rounded-lg border bg-muted/30 text-center">
              <p className="text-sm text-muted-foreground font-semibold">Bay is Empty</p>
            </div>
          )}

          {/* Upcoming Tickets */}
          {upcomingTickets.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-3">
                {isToday(selectedDate) 
                  ? `Upcoming Today (${upcomingTickets.length})`
                  : `Upcoming on ${format(selectedDate, 'MMM d')} (${upcomingTickets.length})`
                }
              </h3>
              <div className="space-y-3">
                {upcomingTickets.map(ticket => (
                  <TicketCard key={ticket.id} ticket={ticket} {...ticketCardProps} />
                ))}
              </div>
            </div>
          )}

          {/* Completed Tickets */}
          {completedTickets.length > 0 && (
            <Collapsible open={showCompleted} onOpenChange={setShowCompleted}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold mb-3 hover:text-primary transition-colors">
                {showCompleted ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {isToday(selectedDate)
                  ? `Completed Today (${completedTickets.length})`
                  : `Completed on ${format(selectedDate, 'MMM d')} (${completedTickets.length})`
                }
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3">
                {completedTickets.map(ticket => (
                  <div key={ticket.id} className="p-3 rounded-lg border bg-muted/20 space-y-2 opacity-70">
                    <div>
                      <p className="font-semibold text-sm">
                        {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
                      </p>
                      <p className="text-xs text-muted-foreground">{ticket.customerName}</p>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Completed: {formatTimeShort(ticket.completed_at || ticket.updated_at, shopTimezone)}</span>
                      {ticket.actual_minutes && ticket.predicted_minutes && (
                        <Badge variant="outline" className="text-xs">
                          {ticket.actual_minutes}m / {ticket.predicted_minutes}m
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* No tickets at all */}
          {!currentTicket && upcomingTickets.length === 0 && completedTickets.length === 0 && (
            <div className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No tickets scheduled for this bay today</p>
            </div>
          )}
        </div>
      </SheetContent>
      {/* Lift Safety Warning Dialog */}
      {liftVerify && (
        <LiftVerificationDialog
          open={!!liftVerify}
          onOpenChange={(o) => { if (!o) setLiftVerify(null); }}
          ticketId={liftVerify.ticketId}
          bayId={liftVerify.bayId}
          bayName={liftVerify.bayName}
          canVerify={!!canEditShopFloor}
          onVerified={() => {
            toast({ title: 'Lift verified', description: 'The vehicle may now go on this lift — move it again.' });
            onTicketReassigned?.();
          }}
        />
      )}
      <LiftCapacityWarning
        open={!!liftWarningData}
        onClose={() => setLiftWarningData(null)}
        gvwr={liftWarningData?.gvwr ?? 0}
        capacity={liftWarningData?.capacity ?? 0}
        compatibleLifts={liftWarningData?.compatibleLifts}
        onSelectLift={async (bayId) => {
          if (!liftWarningData) return;
          const ticketId = liftWarningData.ticketId;
          setLiftWarningData(null);
          await handleReassignBay(ticketId, bayId);
        }}
      />
    </Sheet>
  );
};
